import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Context } from 'Context/Context';
import useIsMobile from 'Hooks/useIsMobile';
import {
  premiumProfCasesPanelItems,
  profCasesPanelItems,
  premiumProfCasesPanelItemsOnAsst,
  profCasesPanelItemsOnAsst,
} from 'Constants';
import SearchContentProf from 'Components/Header/SearchContentProf';
import Footer from 'Components/Header/Footer';

// ---------------------------------------------------------------------------
// Case List (attorney portal) — shared layout pieces from the "Case List"
// design handoff: left case-type panel, mobile section switcher + pills,
// result line, pager, empty state, buttons. Visual only — every list keeps
// its own data calls.
// ---------------------------------------------------------------------------

export const C = {
  navy: '#14213d',
  brass: '#8a6231',
  brassDark: '#6d4c25',
  gold: '#c9a26a',
  cream: '#f7f4ed',
  ivory: '#fffdf8',
  green: '#3a7d4f',
  red: '#9a3b2e',
  font: "'IBM Plex Sans',sans-serif",
};

// Height of the attorney header (Header.jsx, marketplace variant). The left
// panel sticks right below it.
export const HEADER_H = 62;

// Outline icons (24 viewBox) — same paths as the design's ICONS map.
export const RAIL_ICONS = {
  sos: 'M12 3L2 20h20L12 3zM12 10v4M12 17h.01',
  justPosted: 'M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M6 18l2.5-2.5M15.5 8.5L18 6',
  available: 'M3 7h18v13H3zM8 7V4h8v3M3 12h18',
  requested: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
  approved: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8 12l3 3 5-6',
  myCases: 'M3 6h6l2 2h10v12H3zM12 17c0-1.7 1.3-3 3-3s3 1.3 3 3M15 12a1.5 1.5 0 1 0 0-.01',
  notInterested: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM5.6 5.6l12.8 12.8',
  pendingClosing: 'M6 3h12M6 21h12M7 3c0 5 10 5 10 9s-10 4-10 9M17 3c0 5-10 5-10 9',
  closed: 'M3 4h18v4H3zM5 8v12h14V8M10 12h4',
  connectionRequests: 'M16 21v-2a4 4 0 0 0-8 0v2M12 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM19 8v6M22 11h-6',
  funderConnections: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2 21v-1a6 6 0 0 1 12 0v1M16 4.5a3.5 3.5 0 0 1 0 6.5M22 21v-1a6 6 0 0 0-4-5.6',
  overview: 'M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z',
  _: 'M4 5h16M4 12h16M4 19h16',
};

export function Svg({ d, size = 18, stroke = 'currentColor', width = 1.9, style, className, fill = 'none' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" className={'flex-none' + (className ? ' ' + className : '')} style={style}>
      {(Array.isArray(d) ? d : [d]).map((p, i) => <path key={i} d={p} />)}
    </svg>
  );
}

// ---------- small hover tooltip ----------
export function Tip({ text, children, align = 'left', width = 260 }) {
  if (!text) return children;
  return (
    <span className="relative inline-flex group/tip">
      {children}
      <span
        className={`pointer-events-none absolute z-40 opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-opacity top-[calc(100%+8px)] ${align === 'right' ? 'right-0' : 'left-0'} bg-navy text-cream rounded-md py-[9px] px-[11px] font-plexsans font-normal text-[12px] leading-[1.45] shadow-[0_12px_28px_-10px_rgba(20,33,61,.5)] whitespace-normal text-left`}
        style={{ width: width + 'px' }}
      >
        {text}
      </span>
    </span>
  );
}

// ---------- buttons ----------
const BTN_VARIANTS = {
  primary: 'text-ivory bg-brass border-0',
  secondary: 'text-navy bg-white border border-navy/[.25]',
  navy: 'text-ivory bg-navy border-0',
  danger: 'text-danger bg-white border border-danger/[.35]',
  muted: 'text-navy/[.55] bg-navy/[.05] border border-navy/[.12]',
};
// navy/muted: no visible hover (navy's old hover:opacity-90 was always
// overridden by the inline opacity:1, so it never showed).
const BTN_HOVER = {
  primary: 'hover:bg-brassDark',
  secondary: 'hover:bg-cream',
  navy: '',
  danger: 'hover:bg-danger/[.06]',
  muted: '',
};

// `className` is appended after the built-in classes; use `!` (e.g. `!py-2.5`)
// to override a built-in value. `style` still wins over everything.
export function CaseBtn({ variant = 'primary', children, onClick, disabled, full, style, className, title }) {
  const cls = [
    'transition-colors font-plexsans font-semibold text-[13px] leading-none rounded-md py-3 px-[22px] whitespace-nowrap inline-flex items-center justify-center gap-[7px]',
    full ? 'w-full' : '',
    disabled ? 'cursor-not-allowed opacity-[.55]' : 'cursor-pointer',
    BTN_VARIANTS[variant] || '',
    disabled ? '' : BTN_HOVER[variant] || '',
    className || '',
  ].filter(Boolean).join(' ');
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (!disabled && onClick) onClick(e); }}
      className={cls}
      style={style}
    >
      {children}
    </button>
  );
}

// ---------- rail open state (per viewer, remembered) ----------
const RAIL_KEY = 'qcr.caseRailOpen';
const readRail = () => { try { return window.localStorage.getItem(RAIL_KEY) === '1'; } catch (e) { return false; } };
const writeRail = (v) => { try { window.localStorage.setItem(RAIL_KEY, v ? '1' : '0'); } catch (e) { /* ignore */ } };

// ---------- section (main menu) navigation ----------
const toTitle = (label) => (label || '').toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

export function useSections(attorney) {
  const { userDetails, setSelectedMenuItem, setOffset } = useContext(Context);
  const navigate = useNavigate();
  const info = attorney ? attorney?.userId : userDetails;
  const funder = !!info?.isUserFunder;
  const raw = attorney
    ? (info?.isActiveSubscription ? premiumProfCasesPanelItemsOnAsst : profCasesPanelItemsOnAsst)
    : (userDetails?.isActiveSubscription ? premiumProfCasesPanelItems : profCasesPanelItems);
  const sections = (raw || [])
    .filter((i) => ['1', '2', '3', '4', '6'].includes(String(i.key)))
    .map((i) => ({ ...i, label: sectionLabel(String(i.key), funder) || toTitle(i.menu) }));
  const go = (item) => {
    setSelectedMenuItem(item.key);
    setOffset(0);
    navigate(item.link);
  };
  return { sections, go };
}

export function sectionLabel(key, funder) {
  switch (String(key)) {
    case '1': return 'Available Cases';
    case '2': return 'Active Cases';
    case '3': return 'Closed Cases';
    case '4':
    case '6': return funder ? 'Attorney Teamup' : 'Funder Teamup';
    default: return '';
  }
}

// "needs your action" dot - bright coral (#e5654f) so it reads on navy and on the brass selected tab

// ---------- desktop: left case-type panel ----------
// items: [{ key, label, icon, count?, tip?, dot? }]
// dot = the tab needs the user's action (e.g. approved cases waiting for a conflict check)
export function CaseTypeRail({ title, items, activeKey, onSelect }) {
  // pinned = kept open with the >> button (remembered per browser).
  // peek   = opened by hovering while collapsed; floats over the list instead of
  //          pushing it, opens after a short delay and closes when the mouse
  //          leaves or a tab is picked.
  const [pinned, setPinned] = useState(readRail);
  const [peek, setPeek] = useState(false);
  const openT = useRef(null);
  const closeT = useRef(null);
  const clearTimers = () => { clearTimeout(openT.current); clearTimeout(closeT.current); };
  useEffect(() => clearTimers, []);
  const onEnter = () => {
    if (pinned) return;
    clearTimeout(closeT.current);
    openT.current = setTimeout(() => setPeek(true), 150);
  };
  const onLeave = () => {
    clearTimeout(openT.current);
    if (!peek) return;
    closeT.current = setTimeout(() => setPeek(false), 200);
  };
  const toggle = () => {
    clearTimers();
    setPinned((o) => { writeRail(!o); return !o; });
    setPeek(false);
  };
  const pick = (key) => { clearTimers(); setPeek(false); onSelect(key); };
  const open = pinned || peek;
  return (
    <div
      className={`font-plexsans flex-none box-border transition-[width] duration-200 sticky self-start ${pinned ? 'w-[248px]' : 'w-[66px]'} ${peek ? 'z-20' : 'z-[5]'}`}
      style={{ top: HEADER_H + 'px', height: `calc(100vh - ${HEADER_H}px)` }}
    >
    <div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className={`absolute top-0 left-0 bottom-0 box-border bg-navy border-r border-cream/[.1] py-3.5 px-2.5 flex flex-col gap-1 transition-[width,box-shadow] duration-200 overflow-hidden ${open ? 'w-[248px]' : 'w-[66px]'} ${peek ? 'shadow-[12px_0_32px_-8px_rgba(20,33,61,.45)]' : 'shadow-none'}`}
    >
      <div
        onClick={toggle}
        title={pinned ? 'Collapse' : 'Keep open'}
        className="hover:bg-[rgba(247,244,237,0.06)] flex items-center gap-2.5 py-3 px-[13px] mb-1.5 rounded cursor-pointer text-cream/[.55] whitespace-nowrap"
      >
        <Svg d="M13 6l6 6-6 6M5 6l6 6-6 6" width={2} className={`transition-transform duration-200 ${pinned ? 'rotate-180' : ''}`} />
        {open && <span className="font-plexsans font-semibold text-[11px] leading-none tracking-[.08em] uppercase whitespace-nowrap">{title}</span>}
      </div>
      {items.map((t) => {
        const sel = t.key === activeKey;
        return (
          <div
            key={t.key}
            onClick={() => pick(t.key)}
            title={open ? t.tip || undefined : t.label + (t.dot ? ' · needs your action' : '')}
            className={`flex items-center gap-2.5 py-3 px-[13px] rounded cursor-pointer whitespace-nowrap relative ${sel ? 'bg-brass text-ivory' : 'bg-transparent text-cream/[.7]'}`}
          >
            <Svg d={RAIL_ICONS[t.icon] || RAIL_ICONS._} />
            {open && <span className={`flex-1 font-plexsans ${sel ? 'font-semibold' : 'font-medium'} text-[14px] leading-[1.2] overflow-hidden text-ellipsis`}>{t.label}</span>}
            {open && t.count != null &&
              <span className={`font-plexsans font-semibold text-[11.5px] leading-none text-inherit rounded-[10px] py-1 px-2 ${sel ? 'bg-ivory/[.2]' : 'bg-cream/[.12]'}`}>{t.count}</span>}
            {!open && t.dot && <span className="absolute top-[7px] right-2 w-[9px] h-[9px] rounded-full bg-[#e5654f] shadow-[0_0_0_2px_#14213d]" />}
            {open && t.dot && <span className="w-2 h-2 rounded-full bg-[#e5654f] shadow-[0_0_0_2px_#14213d] flex-none" />}
          </div>
        );
      })}
    </div>
    </div>
  );
}

// ---------- mobile: section switcher + case-type pills ----------
function MobileSectionBar({ attorney, sectionKey, items, activeKey, onSelect, setUpdateComp }) {
  const { sections, go } = useSections(attorney);
  const { userDetails } = useContext(Context);
  const funder = !!(attorney ? attorney?.userId?.isUserFunder : userDetails?.isUserFunder);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);
  return (
    <div ref={ref} className="font-plexsans flex-none relative bg-white border-b border-navy/[.1]">
      <div className="flex items-center justify-between pt-3.5 px-4 pb-2.5">
        <span onClick={() => setMenuOpen((o) => !o)} className="flex items-center gap-1.5 font-plexsans font-semibold text-[18px] leading-none text-navy cursor-pointer">
          {sectionLabel(sectionKey, funder)}
          <Svg d="M6 9l6 6 6-6" size={16} width={2.2} stroke={C.navy} className={`transition-transform duration-150 ${menuOpen ? 'rotate-180' : ''}`} />
        </span>
        {!attorney &&
          <span onClick={() => setSearchOpen((o) => !o)} className="flex items-center gap-[5px] font-plexsans font-medium text-[12px] leading-none text-navy/[.5] cursor-pointer">
            <Svg d={['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z', 'M21 21l-4.3-4.3']} size={13} width={2} />
            Search ID
          </span>}
      </div>
      {searchOpen &&
        <div className="pt-0 px-4 pb-2.5">
          <SearchContentProf variant="light" setUpdateComp={setUpdateComp} />
        </div>}
      <div className="hide-vertical-scrollbar flex gap-2 pt-1 px-4 pb-3 overflow-x-auto">
        {items.map((t) => {
          const sel = t.key === activeKey;
          return (
            <span
              key={t.key}
              onClick={() => onSelect(t.key)}
              className={`flex-none flex items-center gap-1.5 font-plexsans font-medium text-[13px] leading-none border rounded-[20px] py-[9px] px-[13px] whitespace-nowrap cursor-pointer ${sel ? 'text-ivory bg-navy border-navy' : 'text-navy bg-white border-navy/[.2]'}`}
            >
              {t.label}
              {t.count != null && <span className="font-plexsans font-semibold text-[11px] leading-none opacity-75">{t.count}</span>}
              {t.dot && <span className="w-2 h-2 rounded-full bg-[#e5654f]" />}
            </span>
          );
        })}
      </div>
      {menuOpen &&
        <div className="absolute top-12 left-3 w-[240px] z-[25] bg-white border border-navy/[.15] rounded-lg shadow-[0_16px_36px_-12px_rgba(20,33,61,.4)] p-1.5">
          {sections.map((m) => {
            const cur = String(m.key) === String(sectionKey);
            return (
              <div
                key={m.key}
                aria-disabled={m.comingSoon ? true : undefined}
                onClick={() => { if (m.comingSoon) return; setMenuOpen(false); if (!cur) go(m); }}
                className={`flex items-center justify-between py-[13px] px-3 rounded-[5px] font-plexsans text-[15px] leading-none ${m.comingSoon ? 'text-navy/[.45] cursor-default' : 'text-navy cursor-pointer'} ${cur ? 'bg-navy/[.06] font-semibold' : 'bg-transparent font-normal'}`}
              >
                {m.label}
                {m.comingSoon &&
                  <span className="font-plexmono font-semibold text-[9.5px] leading-none tracking-[.08em] uppercase rounded-[9px] py-[3px] px-1.5 bg-brass/[.12] text-brass">Soon</span>}
              </div>
            );
          })}
        </div>}
    </div>
  );
}

// ---------- page layout: panel + content ----------
export function CaseListLayout({ attorney, sectionKey, railItems, activeKey, onSelect, setUpdateComp, children }) {
  const isMobile = useIsMobile();
  const { userDetails } = useContext(Context);
  // Desktop: the footer sits in the content column, right of the navy menu,
  // so the menu runs all the way down. The page-wide footer (Layout.jsx) hides
  // itself while this class is on <body>. Phones keep the page-wide footer.
  useEffect(() => {
    if (isMobile) return undefined;
    document.body.classList.add('qcr-rail-footer');
    return () => document.body.classList.remove('qcr-rail-footer');
  }, [isMobile]);
  const funder = !!(attorney ? attorney?.userId?.isUserFunder : userDetails?.isUserFunder);
  if (isMobile) {
    return (
      <div className="w-full font-plexsans bg-cream min-h-[70vh]">
        <MobileSectionBar attorney={attorney} sectionKey={sectionKey} items={railItems} activeKey={activeKey} onSelect={onSelect} setUpdateComp={setUpdateComp} />
        <div className="pt-0 px-3.5 pb-5">{children}</div>
      </div>
    );
  }
  return (
    <div className="flex w-full font-plexsans bg-cream" style={{ minHeight: `calc(100vh - ${HEADER_H}px)` }}>
      <CaseTypeRail title={sectionLabel(sectionKey, funder)} items={railItems} activeKey={activeKey} onSelect={onSelect} />
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="pt-6 px-7 pb-12 flex-1">{children}</div>
        <Footer />
      </div>
    </div>
  );
}

// ---------- "{Case type} · N cases" + optional right side (sort) ----------
export function ResultLine({ label, total, loading, right }) {
  const isMobile = useIsMobile();
  return (
    <div className={`flex justify-between items-center gap-3 min-h-[32px] ${isMobile ? 'mt-[2px] mx-[2px] mb-2.5' : 'mt-0 mx-0 mb-3'}`}>
      <span className={`font-plexsans font-medium ${isMobile ? 'text-[12.5px]' : 'text-[13px]'} leading-none text-navy/[.6]`}>
        {isMobile ? '' : label + ' · '}{loading ? '…' : `${total || 0} ${total === 1 ? 'case' : 'cases'}`}
      </span>
      {right}
    </div>
  );
}

// ---------- cards container: stacked, one outer border ----------
export function CardStack({ children }) {
  const isMobile = useIsMobile();
  if (isMobile) return <div className="flex flex-col gap-3">{children}</div>;
  return <div className="border border-navy/[.12] rounded overflow-hidden bg-white">{children}</div>;
}

// ---------- loading placeholder in the card style ----------
export function CardSkeleton({ count = 2 }) {
  const isMobile = useIsMobile();
  return (
    <CardStack>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`bg-white overflow-hidden ${isMobile ? 'border border-navy/[.12] rounded-lg' : 'border-b border-navy/[.12] rounded-none'}`}>
          <div className={`${isMobile ? 'h-[78px]' : 'h-[66px]'} bg-brass/[.08] border-b border-brass/[.18]`} />
          <div className={`animate-pulse flex flex-col gap-3 ${isMobile ? 'py-4 px-[18px]' : 'pt-5 px-[30px] pb-[22px]'}`}>
            <div className="h-3 w-[40%] bg-navy/[.08] rounded-[3px]" />
            <div className="h-[70px] border border-navy/[.1] rounded-md" />
            <div className="h-3 w-[60%] bg-navy/[.08] rounded-[3px]" />
          </div>
        </div>
      ))}
    </CardStack>
  );
}

// ---------- empty state ----------
export function EmptyState({ title, body, linkText, onLink }) {
  const isMobile = useIsMobile();
  return (
    <div className={`bg-white border border-navy/[.12] ${isMobile ? 'rounded-lg' : 'rounded'}`}>
      <div className={`flex flex-col items-center justify-center gap-3 text-center ${isMobile ? 'py-16 px-5' : 'py-[120px] px-5'}`}>
        <Svg d={['M3 7h18v13H3z', 'M8 7V4h8v3', 'M3 12h18', 'M10 15.5h4']} size={44} width={1.5} stroke="rgba(20,33,61,.3)" />
        <span className="font-plexsans font-semibold text-[16px] leading-[1.3] text-navy">{title}</span>
        {body && <span className="font-plexsans font-normal text-[13.5px] leading-[1.5] text-navy/[.55] max-w-[320px]">{body}</span>}
        {linkText && <span onClick={onLink} className="font-plexsans font-semibold text-[13.5px] leading-none text-brass underline cursor-pointer">{linkText}</span>}
      </div>
    </div>
  );
}

// ---------- previous / next pager ----------
export function CasePager({ offset, limit, total, shown, onPage }) {
  const hasPrev = offset > 0;
  const hasNext = offset + (shown || 0) < (total || 0);
  if (!hasPrev && !hasNext) return null;
  const go = (next) => { window.scrollTo(0, 0); onPage(next); };
  return (
    <div className="font-plexsans flex justify-between items-center gap-3 mt-[18px]">
      <span className="font-plexsans font-medium text-[12.5px] leading-none text-navy/[.55]">
        Showing {offset + 1}–{offset + (shown || 0)} of {total}
      </span>
      <div className="flex gap-2">
        <CaseBtn variant="secondary" disabled={!hasPrev} onClick={() => go(Math.max(0, offset - limit))} className="!py-2.5 !px-4">← Previous</CaseBtn>
        <CaseBtn variant="secondary" disabled={!hasNext} onClick={() => go(offset + limit)} className="!py-2.5 !px-4">Next →</CaseBtn>
      </div>
    </div>
  );
}

// ---------- notice strip inside a card (pending closing, requested before…) ----------
const NOTICE_TONES = {
  info: 'text-navy bg-navy/[.05] border border-navy/[.12]',
  warn: 'text-brassDark bg-brass/[.08] border border-brass/[.25]',
  danger: 'text-danger bg-danger/[.07] border border-danger/[.25]',
};
export function CardNotice({ tone = 'info', children }) {
  return (
    <div className={`inline-flex items-center gap-[7px] font-plexsans font-medium text-[12.5px] leading-[1.4] rounded-[5px] py-[7px] px-[11px] ${NOTICE_TONES[tone] || ''}`}>
      <Svg d={['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 11v5M12 8h.01']} size={14} width={2} />
      <span>{children}</span>
    </div>
  );
}
